import classnames from 'classnames';
import { buildSingleCellId, getIsCellInactive, isFieldWithInactiveMode } from 'components/batchForm/BatchForm.helpers';
import { BatchFormNavLayer } from 'components/batchForm/BatchFormNavLayer/BatchFormNavLayer';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { BatchDragAndDropContext } from 'components/common-form/contexts/batch/BatchDragAndDropContext';
import type { BatchNavigationContextValue } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldType } from 'components/common-form/hooks/useBlockOrFieldType';
import type { FieldMetadata, FieldType } from 'models/forms/fields';
import { FC, useContext, useMemo, useState } from 'react';
import { FormStatus } from 'state/forms/models';
import type { ProductNode } from 'state/product-description/model';
import type { BatchWorkspaceVersion } from 'state/userPreferences/userPreferences.model';

export interface LostInputBeforeEditing {
  keyBoardEvent: KeyboardEventInit;
  cellId: string;
}

interface BatchFormCellProps {
  tabId: string;
  formId: string;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  fieldNode: ProductNode;
  fieldIndex?: number;
  rowIndex: number;
  columnIndex: number;
  isDisabled?: boolean;
  metadata?: FieldMetadata;
  fieldNodeList: ReadonlyArray<ProductNode>;
  formStatus?: FormStatus;
  selectFormCallback: (formId: string) => void;
}

export const BatchFormCell: FC<BatchFormCellProps> = ({
  tabId,
  formId,
  batchWorkspaceVersion,
  fieldNode,
  fieldIndex,
  rowIndex,
  columnIndex,
  isDisabled,
  fieldNodeList,
  metadata,
  formStatus,
  selectFormCallback,
}) => {
  const batchNavigationContext = useContext(BatchNavigationContext);
  const batchDragAndDropContextValue = useContext(BatchDragAndDropContext);

  const [lostInputBeforeEditing, setLostInputBeforeEditing] = useState<string | undefined>(undefined);

  const metadataType = useBlockOrFieldType(fieldNode.id, fieldNode.children);
  const fieldType = fieldNode.type ?? metadataType;
  const canEdit = useMemo<boolean>(() => {
    return (
      isFieldWithInactiveMode(fieldType as FieldType) &&
      !metadata?.isReadOnly &&
      !metadata?.isHidden &&
      formStatus !== 'QUOTING'
    );
  }, [fieldType, metadata, formStatus]);

  const isSelected = useMemo<boolean>(() => {
    return batchNavigationContext.selectedCellId === buildSingleCellId(rowIndex, columnIndex);
  }, [batchNavigationContext, rowIndex, columnIndex]);

  const isCellInactive = useMemo<boolean>(() => {
    return getIsCellInactive(
      batchNavigationContext.isEditing ?? false,
      isSelected,
      isFieldWithInactiveMode(fieldType as FieldType),
    );
  }, [batchNavigationContext.isEditing, fieldType, isSelected]);

  const isFocused = useMemo<boolean | undefined>(() => {
    return isSelected && batchNavigationContext.isEditing;
  }, [batchNavigationContext.isEditing, isSelected]);

  const batchNavigationContextValue = useMemo<BatchNavigationContextValue>(() => {
    return {
      isSelected,
      isCellInactive,
      isFocused,
      lostInputBeforeEditing,
      setLostInputBeforeEditing,
      ...batchNavigationContext,
    } as BatchNavigationContextValue;
  }, [batchNavigationContext, isCellInactive, isFocused, isSelected, lostInputBeforeEditing]);

  const rowNameTranslationKey = `form.${fieldNode.translationKey}`;

  return (
    <BatchNavigationContext.Provider value={batchNavigationContextValue}>
      <BatchDragAndDropContext.Provider value={batchDragAndDropContextValue}>
        <BatchFormNavLayer
          id={fieldNode.id}
          rowNameTranslationKey={rowNameTranslationKey}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          formId={formId}
          className={classnames(
            'batch-grid-cell',
            fieldIndex === fieldNodeList.length - 1 ? 'with-border-bottom-thick' : 'with-border-bottom-thin',
            { 'form-disabled': isDisabled },
          )}
          type={fieldType}
          canEdit={canEdit}
          setLostInputBeforeEditing={setLostInputBeforeEditing}
          metadata={metadata}
          selectFormCallback={selectFormCallback}
          formStatus={formStatus}
        >
          <RenderFormNodeComponent
            batchTabId={tabId}
            tabId={formId}
            level={1}
            workspaceType="newBatch"
            quoteWorkspaceVersion="Advanced"
            node={fieldNode}
            mustForceAdvancedMode
            batchWorkspaceVersion={batchWorkspaceVersion}
            fieldNodeList={fieldNodeList}
            batchColumnIndex={columnIndex}
          />
        </BatchFormNavLayer>
      </BatchDragAndDropContext.Provider>
    </BatchNavigationContext.Provider>
  );
};
